<template>
  <modal
    v-if="history && Object.keys(history).length"
    :errors="{}"
    :title="title"
    class="assignment-normal"
    :class="{ 'assignment-ra': assignmentType === 'read_aloud' }"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <template v-slot:header>
      <div>
        <el-row :gutter="5">
          <el-col :span="3"
            ><strong>{{ $t("common.exporter_title.student_name") }}:</strong></el-col
          >
          <el-col :span="21">{{ userInfo["user_name"] || "" }}</el-col>
        </el-row>
        <el-row :gutter="5">
          <el-col :span="3"
            ><strong>{{ $t("common.exporter_title.student_email") }}:</strong></el-col
          >
          <el-col :span="21">{{ userInfo["user_email"] || "" }}</el-col>
        </el-row>
      </div>
    </template>
    <template v-slot:body>
      <el-table
        v-if="assignmentType === 'read_aloud'"
        :data="history[assignmentType]"
        :default-sort="{ prop: 'timestamp', order: 'descending' }"
        :empty-text="$t('common.no_values')"
        class="assignment-history"
        header-row-class-name="assignment-header"
      >
        <el-table-column :label="$t('assignments.details.date_timestamp')" prop="timestamp" sortable min-width="200">
          <template slot-scope="scope">
            <div class="date-completed">
              <p class="date">
                {{ dateFormatter(scope.row.timestamp) }}
              </p>
              <div class="tag-wrapper">
                <chip-rounded
                  v-show="scope.row.status"
                  :class="getChipClass(scope.row.status)"
                  class="chip-rounded--small"
                >
                  {{ scope.row.status }}
                </chip-rounded>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          v-if="hasWarmUpSS"
          :label="$t('assignments.details.overall_score')"
          align="center"
          sortable
          prop="eps_score"
        >
          <template slot-scope="scope">
            {{ formatScore((scope.row.eps_score + 100) / 2) }}
          </template>
        </el-table-column>
        <base-table-column
          v-else
          :formatter="percentageFormatter"
          :label="$t('assignments.details.overall_score')"
          align="center"
          prop="eps_score"
          sortable
        />
        <el-table-column :label="$t('assignments.details.warm_up_study_set')" align="center" width="150" v-if="hasWarmUpSS">
          <template slot-scope="scope">
            {{ scope["$index"] === history[assignmentType].length - 1 ? "100%" : "" }}
          </template>
        </el-table-column>
        <base-table-column
          :formatter="percentageFormatter"
          :label="$t('assignments.details.eps')"
          align="center"
          prop="eps_score"
          sortable
        />
        <base-table-column
          :formatter="percentageFormatter"
          :label="$t('assignments.details.fluency')"
          align="center"
          prop="fluency_score"
          sortable
        />
        <base-table-column
          :formatter="percentageFormatter"
          :label="$t('assignments.details.pronunciation')"
          align="center"
          prop="pronunciation_score"
          sortable
        />
        <base-table-column
          :formatter="percentageFormatter"
          :label="$t('assignments.details.intonation')"
          align="center"
          prop="intonation_score"
          sortable
        />
      </el-table>
      <el-table
        v-else-if="assignmentType === 'lesson'"
        :data="formatLessonsScore(history[assignmentType])"
        :default-sort="{ prop: 'timestamp', order: 'descending' }"
        :empty-text="$t('common.no_values')"
        class="assignment-history"
        header-row-class-name="assignment-header"
      >
        <el-table-column :label="$t('assignments.details.date_timestamp')" prop="timestamp" sortable>
          <template slot-scope="scope">
            <div class="date-completed">
              <p class="date">
                {{ dateFormatter(scope.row.timestamp) }}
              </p>
              <div class="tag-wrapper">
                <chip-rounded
                  v-show="scope.row.status"
                  :class="getChipClass(scope.row.status)"
                  class="chip-rounded--small"
                >
                  {{ scope.row.status }}
                </chip-rounded>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('assignments.details.overall_score')"
          align="center"
          prop="average_score"
          sortable
        />
        <el-table-column
          v-for="(lessonId, idx) in userInfo['lessonIds']"
          :key="idx"
          :label="`Lesson ${idx + 1}`"
          :prop="`lesson_${lessonId}`"
          align="center"
          class-name="activity-cell"
        />
      </el-table>
      <div class="actions">
        <primary-button @click.native="closeModal">
          OK
        </primary-button>
      </div>
    </template>
  </modal>
</template>

<script>
import { isNil, unless } from "ramda";
import { mapGetters } from "vuex";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import Modal from "@/components/common/Modal";
import PrimaryButton from "@/components/common/buttons/PrimaryButton";
import percentage from "@/components/common/mixins/percentage";
import ChipRounded from "@/components/common/ChipRounded.vue";
import BaseTableColumn from "@/components/classes/BaseTableColumn";
import { SUBMISSION_LATE, SUBMISSION_MISSED } from "@/constants/generic";

export default {
  components: {
    ChipRounded,
    Modal,
    PrimaryButton,
    BaseTableColumn,
  },
  props: {
    userInfo: {
      type: Object,
    },
    title: {
      type: String,
    },
    history: {
      type: Object,
    },
    assignmentType: {
      type: String,
    },
    hasWarmUpSS: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      currentOrganization: "organization/currentOrganization",
    }),
  },
  data() {
    return {
      dateFormat: "HH:mm a, MM/dd/yyyy",
    };
  },
  methods: {
    closeModal() {
      this.$emit("closed");
    },
    percentageFormatter(row, column, cellValue) {
      if (cellValue === 0) return null;
      return percentage.filters.toPercentage(unless(isNil, Math.round)(cellValue));
    },
    dateFormatter(date) {
      if (isNaN(Date.parse(date))) {
        return date;
      }
      const zonedDate = utcToZonedTime(date, this.currentOrganization.timezone);
      return format(zonedDate, this.dateFormat);
    },
    getChipClass(status) {
      switch (status) {
        case SUBMISSION_MISSED:
          return "chip-rounded--red";
        case SUBMISSION_LATE:
          return "chip-rounded--orange";
        default:
          return "";
      }
    },
    formatScore(score) {
      if (isNaN(score)) {
        return "--";
      } else if (score === 0) {
        return "0%";
      } else {
        return Math.round(score) + "%";
      }
    },
    formatLessonsScore(input) {
      const lessonIds = this.userInfo["lessonIds"];
      let output = [];
      let studyAttempts = {};
      let highestScores = {};

      // Group input by study_attempt
      input.forEach(entry => {
        const attempt = entry.study_attempt;
        if (!studyAttempts[attempt]) {
          studyAttempts[attempt] = [];
        }
        studyAttempts[attempt].push(entry);
      });

      // Loop through study_attempts
      for (const attempt in studyAttempts) {
        const attemptEntries = studyAttempts[attempt];
        const currentScores = { ...highestScores };

        // Loop through entries in the current study_attempt
        attemptEntries.forEach(entry => {
          const lessonId = entry["lesson_original_id"];
          const score = entry["score"];

          // Check if lessonId already has a recorded score
          if (!highestScores[lessonId] || (score && highestScores[lessonId] < score)) {
            highestScores[lessonId] = score;
          }

          currentScores[lessonId] = score;
        });

        // Calculate average score for the current study_attempt
        const averageScore =
          lessonIds.reduce((sum, lessonId) => sum + (currentScores[lessonId] || 0), 0) / lessonIds.length;

        // Create output object for the current study_attempt
        let outputObj = {
          timestamp: attemptEntries[0]["timestamp"],
          status: attemptEntries[0]["status"],
          average_score: this.formatScore(averageScore),
        };

        // Add scores for each lessonId to the output object
        lessonIds.forEach(lessonId => {
          outputObj[`lesson_${lessonId}`] = this.formatScore(currentScores[lessonId]) || "--";
        });

        // Add the output object to the final output array
        output[attempt] = outputObj;
      }

      return output;
    },
  },
};
</script>

<style lang="scss" scoped>
.assignment-history {
  padding-bottom: 10px !important;
}
.actions {
  align-items: flex-start;
  .el-button {
    padding: 12px;
  }
}
.assignment-normal {
  ::v-deep .el-dialog.elsa-modal {
    width: 90%;
  }
}
.assignment-ra {
  ::v-deep .el-dialog.elsa-modal {
    width: 82%;
  }
}
.date-completed {
  display: flex;
  align-items: center;
  .date {
    width: 190px;
  }
  .tag-wrapper {
    min-width: 50px;
    margin-left: 10px;
  }
}
::v-deep .el-table .el-table__header-wrapper .assignment-header .is-leaf {
  background-color: #f9fbfd !important;
}

::v-deep .chip-rounded--small {
  min-width: 60px;
}
</style>
